// connectionsBlade.js — the Connections layer for the BLADE scene modal.
//
// WHY THIS FILE EXISTS AT ALL
// ---------------------------
// Connections shipped in the React Layered Read panel (`Pages/Sos2/NodePanel.js` +
// `ConnectionsTab.js`) and nowhere else. The Blade scene modal — the surface a writer actually
// reaches from the scene table, the cards, the ribbon and every deep link — had none of it, even
// though it is now the ONLY place where both instruments sit as sibling tabs. That adjacency is
// the entire point of the feature ("the writer performing the jump between the two views IS the
// lesson", SCENE_SCREEN_DESIGN_BRIEF §1), so it is the surface the layer most needs to be on.
//
// ⛔ THE GATE IS IMPORTED, NEVER REIMPLEMENTED. `pairingGate.js` is the product's boundary: a
// pairing renders only if a human approval hash still matches a canonical digest of its semantic
// content plus generator provenance. It is pure, it has ten selftests runnable with bare `node`,
// and it is the single thing standing between "reviewed" and "the model said so". Writing a
// second copy of that check — in PHP for Blade, or inline in a <script> — would be two
// implementations of one invariant, and the second one is the one that drifts. So this file is a
// renderer with no opinions about renderability: `gatePairings()` decides, always.
//
// ⛔ THIS FILE WRITES NO CROSS-INSTRUMENT PROSE. Every sentence a reader sees about how two
// readings relate — the shared-moment label and the mechanism — is printed VERBATIM out of the
// approved payload. The only strings authored here are structural chrome (a heading, a
// vocabulary name, a button label) that make no claim about any particular scene. A freshly
// worded cross-instrument sentence is a new bridge claim and has to go through generation under
// the frozen prompt tuple, human review and hashing — see SCENE_SCREEN_DESIGN_BRIEF §5, which
// calls this out precisely because it looks like a copy choice and is a product-integrity
// boundary.
//
// ⛔ ◇ CARRIES EXACTLY ONE MEANING: "another approved reading found this same page moment". Never
// good/bad, never agreement strength, never confidence. Do not colour it by score, do not size it
// by count, do not sort by it.
//
// ⚠️ ABSENCE, NOT AN EMPTY STATE. Most scenes have nothing approved (2026-08-26: 31 approved
// patterns across 10 scenes of 5 movies, corpus-wide). Every mount point Blade emits is an
// unstyled empty element; this file only ever ADDS. A scene with no approved pairings must show
// no chip, no ◇, no module, no heading and no gap — the feature simply does not exist there.
//
// WHAT IS DELIBERATELY NOT PORTED
// -------------------------------
// The React panel's "◇ N shared findings" header chip + drawer. In that panel the chip was the
// only discovery entry point, because there was no orientation layer above the tabs. Here the
// orientation block (§5.3, rendered at the top of the modal) IS that drawer, permanently open and
// above the fold, so a chip beside it would be a second entry point to a list already on screen.

'use strict';

import { gatePairings, writerEvent } from './Pages/Sos2/pairingGate';

const NAVY  = '#14536e';   // Scene Mechanics side — the modal's own navy, not NodePanel's #1f3a5f
const CRAFT = '#6d4c8f';   // Craft Read side — the accent scene_craft_read.blade.php already uses

/* ── transport ─────────────────────────────────────────────────────────────────────────────── */

function csrf() {
  const m = document.querySelector('meta[name="csrf-token"]');
  return m ? m.getAttribute('content') : null;
}

// Fire-and-forget. The writer-action log is an audit trail (#108, append-only); it is not allowed
// to become a UX dependency, so a failed POST must never surface or block the interaction.
function sendEvent(pairing, action, extra) {
  let ev;
  try { ev = writerEvent(pairing, action, extra || null); }
  catch (e) { return; }                      // unknown action — writerEvent throws by design
  const t = csrf();
  fetch('/sos2/connections/event', {
    method: 'POST',
    headers: Object.assign(
      { 'Content-Type': 'application/json', Accept: 'application/json' },
      t ? { 'X-CSRF-TOKEN': t } : {},
    ),
    body: JSON.stringify(ev),
  }).catch(() => {});
}

// One request per scene, at most, and only after the modal is opened. A 60-scene analysis page
// renders 60 modals; fetching on load would be 60 requests for a feature that is absent from most
// of them. The promise itself is cached so a re-open is free and two fast opens do not race.
const cache = new Map();
function loadPairings(movieId, sceneNumber) {
  const key = movieId + '/' + sceneNumber;
  if (cache.has(key)) return cache.get(key);
  const p = fetch(`/sos2/connections/pairings?movie_id=${encodeURIComponent(movieId)}&scene_number=${encodeURIComponent(sceneNumber)}`,
    { headers: { Accept: 'application/json' } })
    .then(r => (r.ok ? r.json() : { enabled: false, pairings: [] }))
    .then(async d => {
      // {enabled:false} is the server saying the feature does not exist for this account — a
      // different thing from "approved nothing", and both render identically (nothing).
      if (!d || !d.enabled) return [];
      const { renderable } = await gatePairings(d.pairings || []);
      return renderable;
    })
    .catch(() => []);
  cache.set(key, p);
  return p;
}

/* ── grouping (ported from ConnectionsTab.js so both surfaces group identically) ────────────── */

// key -> [{ pairing, links }]. A pattern with two links on the same key yields ONE module holding
// both counterparts; a pattern spanning two keys appears under each, showing only its own links.
function groupBy(pairings, field) {
  const out = {};
  for (const p of pairings) {
    const mine = {};
    for (const l of (p.pairings || [])) (mine[l[field]] = mine[l[field]] || []).push(l);
    for (const k of Object.keys(mine)) (out[k] = out[k] || []).push({ pairing: p, links: mine[k] });
  }
  return out;
}

/* ── DOM helpers ───────────────────────────────────────────────────────────────────────────── */

function el(tag, cls, text) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;      // textContent everywhere: approved copy is DATA
  return n;
}

// ── HOVER TEXT ────────────────────────────────────────────────────────────────────────────────
// ⚠️ NEVER THE BARE `title` ATTRIBUTE ON A DESIGNED SURFACE (CLAUDE.md, owner directive
// 2026-08-24): the native tip waits about a second, renders tiny and unstyled, and reads as a bug
// next to everything else in this modal. `title` is still SET, as the no-JS fallback — Bootstrap
// consumes it on init, so the native tip never double-fires.
// ⚠️ THE SINGLE-OPEN GUARD IS NOT OPTIONAL HERE. A rail is a strip of hover targets, and every
// Bootstrap tooltip appends its own popper to <body>; dragging the pointer down twelve rows opens
// twelve of them and they stack. Three things together fix it and all three are needed: no
// animation (the fade is what orphans nodes on fast traversal), a show delay (passing OVER a
// target should open nothing; only settling on one should), and an explicit guard that hides the
// previously open instance.
let openTip = null;
function tip(node, text) {
  node.setAttribute('title', text);
  if (!window.bootstrap || !window.bootstrap.Tooltip) return node;   // JS-off / no Bootstrap: title stands
  if (window.bootstrap.Tooltip.getInstance(node)) return node;
  const t = new window.bootstrap.Tooltip(node, {
    animation: false,
    delay: { show: 70, hide: 0 },
    container: 'body',
    customClass: 'sr-conv-tip',
  });
  node.addEventListener('show.bs.tooltip', () => {
    if (openTip && openTip !== t) { try { openTip.hide(); } catch (e) { /* already gone */ } }
    openTip = t;
  });
  return node;
}

// ⚠️ THE COUNTERPART'S NAME IS READ OFF THE RAIL, NOT TITLE-CASED FROM ITS KEY. A module that
// says "Character Changes" while the rail two inches away says "Character changes" reads as two
// different things to someone learning the vocabulary — and teaching the vocabulary is the
// feature. The rail row is the on-screen source of truth for what a measure is called.
function axisLabel(modal, key) {
  const row = modal.querySelector(`[data-conv-axis="${CSS.escape(key)}"] .sm-rail-name`);
  return row ? row.textContent.trim() : key.replace(/_/g, ' ');
}
function criterionLabel(modal, key) {
  const row = modal.querySelector(`[data-conv-criterion="${CSS.escape(key)}"] .cr-rail-name`);
  return row ? row.textContent.trim() : key.replace(/_/g, ' ');
}

// Switch tabs inside THIS modal and select a measure. Scoped to the modal on purpose: the older
// tab buttons in scene_modals.blade.php carry ids that are NOT scene-suffixed (`basics-tab`), so
// a document-level lookup would hit whichever scene's modal rendered first.
function jumpTo(modal, side, key) {
  const n = modal.getAttribute('data-conn-scene');
  const tabSel = side === 'craft' ? `[data-bs-target="#basics${n}"]` : `[data-bs-target="#der${n}"]`;
  const tab = modal.querySelector(tabSel);
  if (!tab) return false;
  tab.click();
  // The radio id is the rail's own key, which for Craft Read is NOT the bridge key — the label
  // carrying data-conv-criterion knows its own `for`, so ask the DOM rather than re-deriving the
  // mapping that craft_read_bridge_key() already owns on the PHP side.
  let radioId = null;
  if (side === 'craft') {
    const lab = modal.querySelector(`[data-conv-criterion="${CSS.escape(key)}"]`);
    radioId = lab ? lab.getAttribute('for') : null;
  } else {
    radioId = `sm-${n}-${key}`;
  }
  const radio = radioId ? modal.querySelector('#' + CSS.escape(radioId)) : null;
  if (radio) {
    radio.checked = true;
    // The panels are CSS-only (`:checked ~`), so nothing listens for a change — but the scroll
    // does, and the spotlight it should scroll to only exists after the tab is shown.
    setTimeout(() => {
      const lab = modal.querySelector(`label[for="${CSS.escape(radioId)}"]`);
      if (lab && lab.scrollIntoView) lab.scrollIntoView({ block: 'nearest' });
    }, 60);
  }
  return true;
}

/* ── "Show me on the page" ─────────────────────────────────────────────────────────────────── */

// Reveal the scene text and highlight the quotation. Quotations stay verbatim — this only FINDS,
// it never edits what the writer wrote.
//
// ⚠️ THE SEARCH RUNS ACROSS TEXT NODES, NOT WITHIN ONE. The scene text is nl2br'd, so it parses
// as one text node per line with <br>s between; a quotation of any length lives in no single node.
// Measured on the approved corpus (2026-08-26): 44 quotations, median 68 chars, 14 over 120, and
// the longest 1044 — so a per-node matcher would have found only the short ones and quietly failed
// on exactly the long passages a writer most needs pointing at. The fix is to match against the
// CONCATENATION with an offset index, then wrap with a Range, which splits nodes for us.
//
// ⚠️ AND IT FALLS BACK TO THE OPENING PHRASE, because 8 of 44 quotations contain an ELISION
// ("Amelie freezes, mouth agape ... Lightning illumines the sky"). An elided quotation matches no
// contiguous run of the page by construction, so the full-quote pass can never succeed on it. The
// opening phrase is still verbatim page text and still lands the reader on the right passage.
// Order matters: whole quote first, phrase only as a fallback, so the precise highlight wins when
// it is available. A missed highlight still switches the view and scrolls — a dead button is the
// worst outcome and this surface has shipped that failure before.
function findRange(box, needle) {
  const nodes = [];
  const walker = document.createTreeWalker(box, NodeFilter.SHOW_TEXT);
  let node, all = '';
  while ((node = walker.nextNode())) { nodes.push({ node, start: all.length }); all += node.nodeValue; }
  if (!all) return null;
  const esc = w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const attempt = (words) => {
    if (!words.length) return null;
    let m;
    try { m = all.match(new RegExp(words.map(esc).join('[\\s]+'))); }
    catch (e) { return null; }
    return m ? { from: m.index, to: m.index + m[0].length } : null;
  };
  const words = needle.split(/\s+/).filter(Boolean);
  const span = attempt(words) || attempt(words.slice(0, 8));
  if (!span) return null;
  const locate = (offset) => {
    for (let i = nodes.length - 1; i >= 0; i--) {
      if (offset >= nodes[i].start) return [nodes[i].node, offset - nodes[i].start];
    }
    return [nodes[0].node, 0];
  };
  const r = document.createRange();
  const [sn, so] = locate(span.from);
  const [en, eo] = locate(span.to);
  try { r.setStart(sn, so); r.setEnd(en, eo); } catch (e) { return null; }
  return r;
}

function showOnPage(modal, quote) {
  const n = modal.getAttribute('data-conn-scene');
  const fn = window['showSceneAnalysis' + n];
  if (typeof fn === 'function') fn(false);                 // false = show the script, not the analysis
  const sw = document.getElementById('scenemodalscustomSwitch' + n);
  if (sw) sw.checked = true;

  const box = document.getElementById('scenemodals_script' + n);
  if (!box) return;

  // Un-wrap any previous highlight before searching, or the marks accumulate across clicks and
  // the second search would be running over its own <mark> boundaries.
  box.querySelectorAll('mark.sr-conv-hl').forEach(old => {
    const parent = old.parentNode;
    while (old.firstChild) parent.insertBefore(old.firstChild, old);
    old.remove();
    parent.normalize();
  });

  let hit = null;
  const needle = String(quote || '').trim();
  if (needle) {
    const range = findRange(box, needle);
    if (range) {
      const mark = el('mark', 'sr-conv-hl');
      try { mark.appendChild(range.extractContents()); range.insertNode(mark); hit = mark; }
      catch (e) { hit = null; }     // a range we cannot wrap is not worth mangling the page over
    }
  }
  const target = hit || box;
  if (target.scrollIntoView) target.scrollIntoView({ block: 'center', behavior: 'smooth' });
}

/* ── the convergence module ────────────────────────────────────────────────────────────────── */

// One approved pattern, seen from ONE side. `side` is the side we are RENDERING IN, so the module
// names the OTHER vocabulary — that jump is the lesson.
function buildModule(modal, group, side, onHide) {
  const p = group.pairing;
  const craftSide = side === 'craft';
  const otherName = craftSide ? 'Scene Mechanics' : 'Craft Read';
  const accent    = craftSide ? NAVY : CRAFT;
  const otherKey  = l => (craftSide ? l.scene_mechanics_axis : l.craft_read_criterion);
  const labelOf   = k => (craftSide ? axisLabel(modal, k) : criterionLabel(modal, k));

  const wrap = el('div', 'sr-conv');
  wrap.style.setProperty('--conv-accent', accent);

  // eyebrow — the corroboration claim, and nothing more than the corroboration claim
  const head = el('div', 'sr-conv-head');
  const eyebrow = el('span', 'sr-conv-eyebrow', '◇ Also seen in ' + otherName);
  tip(eyebrow, 'Another approved reading found this same page moment');
  head.appendChild(eyebrow);

  const hideBtn = el('button', 'sr-conv-hide', 'Hide');
  hideBtn.type = 'button';
  hideBtn.addEventListener('click', () => { sendEvent(p, 'hide'); onHide(p.approval.approved_key); });
  head.appendChild(hideBtn);
  wrap.appendChild(head);

  // the other vocabulary — only the NEW information; the reader already knows where they are
  const names = el('div', 'sr-conv-names');
  group.links.forEach((l, i) => {
    const k = otherKey(l);
    if (i) names.appendChild(el('span', 'sr-conv-sep', ' · '));
    // A criterion this rail has no row for (theme, surface clarity, scene continuation) still
    // gets NAMED — the finding is real on the Scene Mechanics side — it just cannot be jumped to.
    const jumpable = craftSide
      ? !!modal.querySelector(`[data-conv-axis="${CSS.escape(k)}"]`)
      : !!modal.querySelector(`[data-conv-criterion="${CSS.escape(k)}"]`);
    // ⚠️ THE SAME CONTROL AS THE ORIENTATION BLOCK'S, DELIBERATELY IDENTICAL. The first cut styled
    // this one as a bold coloured word with a chevron (the React module's treatment) while the
    // orientation used pills — two looks for one action, inside one modal, which is the promise
    // consistency makes and breaks. Unified on the PILL because affordance decides the tie: a bold
    // coloured word is exactly the shape this surface has shipped four times as something that
    // looked live and was not (landmines 125, 128, 145). It is sized up here because in a spotlight
    // this is the module's most important element; the shape is shared, the emphasis is not.
    const cls = 'sr-conv-chip ' + (craftSide ? 'is-mech' : 'is-craft') + ' is-lg';
    if (jumpable) {
      const b = el('button', cls, labelOf(k) + ' ›');
      b.type = 'button';
      tip(b, 'See what ' + otherName + ' says here');
      b.addEventListener('click', () => jumpTo(modal, craftSide ? 'mechanics' : 'craft', k));
      names.appendChild(b);
    } else {
      names.appendChild(el('span', cls + ' is-flat', labelOf(k)));
    }
  });
  wrap.appendChild(names);

  // the page moment — approved copy, verbatim
  wrap.appendChild(el('div', 'sr-conv-label', p.shared_moment.label));

  const quotes = p.shared_moment.quotations || [];
  const primary = quotes[0];
  if (primary) {
    const bq = el('blockquote', 'sr-conv-quote', '“' + primary.text + '”');
    bq.dataset.convQuote = primary.text;   // read by dedupeQuotes() once the module is in the DOM
    wrap.appendChild(bq);
  }
  const rest = quotes.slice(1);
  if (rest.length) {
    const more = el('button', 'sr-conv-more', '+ ' + rest.length + ' related excerpt' + (rest.length > 1 ? 's' : ''));
    more.type = 'button';
    more.addEventListener('click', () => {
      more.remove();
      rest.forEach(q => wrap.insertBefore(el('blockquote', 'sr-conv-quote is-rest', '“' + q.text + '”'), why));
    });
    wrap.appendChild(more);
  }

  // the payoff: why the two readings meet. Approved copy, verbatim, one line per link.
  const why = el('div', 'sr-conv-why');
  group.links.forEach(l => why.appendChild(el('p', null, l.why)));
  wrap.appendChild(why);

  const foot = el('div', 'sr-conv-foot');
  if (primary) {
    // The one real action, and the only one styled as a button: it continues the writer's task
    // rather than commenting on it.
    const show = el('button', 'sr-conv-show', '⌖ Show me on the page');
    show.type = 'button';
    show.addEventListener('click', () => { sendEvent(p, 'show_on_page'); showOnPage(modal, primary.text); });
    foot.appendChild(show);
  }
  const votes = el('span', 'sr-conv-votes');
  [['helpful', '👍', 'Helpful'], ['not_helpful', '👎', 'Not helpful']].forEach(([action, glyph, label]) => {
    const b = el('button', 'sr-conv-vote', glyph);
    b.type = 'button';
    b.setAttribute('aria-label', label);
    tip(b, label);
    b.addEventListener('click', () => {
      sendEvent(p, action);
      votes.querySelectorAll('.sr-conv-vote').forEach(x => x.classList.remove('is-on'));
      b.classList.add('is-on');
    });
    votes.appendChild(b);
  });
  foot.appendChild(votes);
  wrap.appendChild(foot);

  return wrap;
}

/* ── the orientation block (SCENE_SCREEN_DESIGN_BRIEF §5.3) ─────────────────────────────────── */

// Composes approved rows; authors nothing. Each row is one approved pattern: the page moment in
// the writer's own words (verbatim), the two vocabularies that met on it, and the mechanism
// sentence (verbatim). Clicking a row opens that measure's panel in the tab it belongs to.
function buildOrientation(modal, pairings, hidden, onHide) {
  const visible = pairings.filter(p => !hidden.has(p.approval.approved_key));
  if (!visible.length) return null;

  const box = el('section', 'sr-conv-orient-box');
  const h = el('h4', 'sr-conv-orient-h', 'Where both readings meet');
  box.appendChild(h);
  // The ONE authored sentence in this block. It describes the mechanism of the FEATURE — what a
  // row is and how it got there — and makes no claim about this scene, this script, or any
  // relationship between the two instruments' scores. Keep it that way.
  box.appendChild(el('p', 'sr-conv-orient-sub',
    visible.length === 1
      ? 'One moment on this page that both readings picked out. Reviewed before it appeared here.'
      : visible.length + ' moments on this page that both readings picked out. Reviewed before they appeared here.'));

  visible.forEach(p => {
    const row = el('div', 'sr-conv-orient-row');
    const axes = [...new Set((p.pairings || []).map(l => l.scene_mechanics_axis))];
    const crit = [...new Set((p.pairings || []).map(l => l.craft_read_criterion))];

    // ⚠️ THE TWO INSTRUMENTS ARE NAMED ON EVERY ROW, ON THEIR OWN LINE, and this is not decoration.
    // The first cut put both sides on one line separated by ◇, and on real data that produced
    // "Freshness · Unpredictability ◇ Originality · Unpredictability" (9179 s44) — the SAME WORD on
    // both sides, because both batteries happen to carry an Unpredictability measure. Unlabelled,
    // that reads as a duplication bug rather than as the point. Colour alone cannot carry it: a
    // reader meeting these names for the first time has no idea navy means one instrument and
    // purple the other, and this block exists precisely to teach that.
    // ⚠️ The labels are rendered at chip size in the side's own accent, not as small grey eyebrows.
    // A label must never be less visible than the thing it labels (CLAUDE.md, owner pet peeve).
    const side = (name, cls, keys, jump, labeller) => {
      const line = el('div', 'sr-conv-orient-side');
      line.appendChild(el('span', 'sr-conv-orient-instr ' + cls, name));
      const list = el('span', 'sr-conv-orient-list');
      keys.forEach((k, i) => {
        if (i) list.appendChild(el('span', 'sr-conv-sep', ' · '));
        const jumpable = jump(k);
        if (jumpable) {
          const b = el('button', 'sr-conv-chip ' + cls, labeller(k));
          b.type = 'button';
          b.addEventListener('click', jumpable);
          list.appendChild(b);
        } else {
          list.appendChild(el('span', 'sr-conv-chip is-flat ' + cls, labeller(k)));
        }
      });
      line.appendChild(list);
      return line;
    };
    const vocab = el('div', 'sr-conv-orient-vocab');
    vocab.appendChild(side('Scene Mechanics', 'is-mech', axes,
      k => (() => jumpTo(modal, 'mechanics', k)),
      k => axisLabel(modal, k)));
    vocab.appendChild(side('Craft Read', 'is-craft', crit,
      k => (modal.querySelector(`[data-conv-criterion="${CSS.escape(k)}"]`) ? (() => jumpTo(modal, 'craft', k)) : null),
      k => criterionLabel(modal, k)));
    row.appendChild(vocab);
    row.appendChild(el('div', 'sr-conv-orient-label', p.shared_moment.label));
    // ONE mechanism sentence, verbatim, from the first link — not a summary of several, which
    // would be a new cross-instrument claim written in the view layer. When a pattern has more
    // links, the rest are read in the panels the chips above lead to.
    const first = (p.pairings || [])[0];
    if (first && first.why) row.appendChild(el('p', 'sr-conv-orient-why', first.why));
    box.appendChild(row);
  });

  return box;
}

/* ── render ────────────────────────────────────────────────────────────────────────────────── */

// ⚠️ DO NOT PRINT THE SAME PAGE LINE TWICE IN ONE PANEL. Both instruments quote the page and they
// frequently land on the SAME line: on 9179 s44 the Freshness spotlight's own evidence blockquote
// and the convergence module's primary quotation were word-for-word identical, rendered 200px
// apart. Two identical quotes read as a rendering bug, and they bury what the module is actually
// adding — the other vocabulary and the mechanism sentence.
//
// ⚠️ THIS IS A DISPLAY DECISION AND MUST STAY ONE. The payload is untouched, so the approval hash
// is unaffected (canonicalContent() covers the quotation text, not whether we drew it), and "Show
// me on the page" still targets the full verbatim string. Never "dedupe" by editing a quotation.
//
// It has to run AFTER insertion, because the test is "is this text already visible in the panel
// this module landed in" — which no module can answer while it is still detached.
function dedupeQuotes(scope) {
  const norm = s => s.replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/\s+/g, ' ').trim();
  scope.querySelectorAll('.sr-conv-quote[data-conv-quote]').forEach(bq => {
    const host = bq.closest('.sm-spot, .cr-spot');
    if (!host) return;
    const near = [...host.querySelectorAll('.sm-spot-ev, .cr-example, .cr-read')]
      .map(n => norm(n.textContent)).join('   ');
    if (near.includes(norm(bq.dataset.convQuote))) bq.remove();
  });
}

function render(modal, pairings) {
  const hidden = modal.__convHidden || (modal.__convHidden = new Set());

  // Idempotent: a modal can be opened many times, and re-rendering must never stack duplicates.
  modal.querySelectorAll('.sr-conv, .sr-conv-orient-box').forEach(n => n.remove());

  const live = pairings.filter(p => !hidden.has(p.approval.approved_key));
  const redraw = () => render(modal, pairings);
  const onHide = (key) => { hidden.add(key); redraw(); };

  // 1. orientation, above the tabs
  const mount = modal.querySelector('[data-conv-orient]');
  if (mount) {
    mount.textContent = '';
    const box = buildOrientation(modal, pairings, hidden, onHide);
    if (box) mount.appendChild(box);
  }

  // 2. the modules, in both spotlights
  const byAxis = groupBy(live, 'scene_mechanics_axis');
  const byCrit = groupBy(live, 'craft_read_criterion');
  modal.querySelectorAll('.sr-conv-slot').forEach(slot => {
    slot.textContent = '';
    const side = slot.getAttribute('data-conv-side');
    const key  = slot.getAttribute('data-conv-key');
    const groups = (side === 'craft' ? byCrit : byAxis)[key] || [];
    groups.forEach(g => slot.appendChild(buildModule(modal, g, side, onHide)));
  });
  dedupeQuotes(modal);

  // 3. ◇ on connected rail rows. Presence only — never a count, never a colour, never a sort key.
  //
  // ⚠️ A CLASS, NOT AN APPENDED NODE. Both rails are CSS grids with an explicit
  // `grid-template-columns` (`96px 1fr 26px 10px` on Scene Mechanics), so appending a fifth child
  // to a row creates an implicit column and silently re-lays-out every row that has one — the mark
  // would have shifted the score column on exactly the rows it was meant to annotate. The class
  // drives a `::after` on the name cell instead, which costs no track.
  modal.querySelectorAll('.has-conv').forEach(n => n.classList.remove('has-conv'));
  const mark = (attr, keys) => keys.forEach(k => {
    const row = modal.querySelector(`[${attr}="${CSS.escape(k)}"]`);
    if (row) { row.classList.add('has-conv'); tip(row, 'Also identified in the other reading'); }
  });
  mark('data-conv-axis', Object.keys(byAxis));
  mark('data-conv-criterion', Object.keys(byCrit));
}

/* ── boot ──────────────────────────────────────────────────────────────────────────────────── */

function attach(modal) {
  if (modal.__convBound) return;
  modal.__convBound = true;
  const movieId = modal.getAttribute('data-conn-movie');
  const scene   = modal.getAttribute('data-conn-scene');
  if (!movieId || !scene) return;
  loadPairings(movieId, scene).then(list => {
    if (!list.length) return;              // absence: nothing is added, nothing is reserved
    render(modal, list);
  });
}

document.addEventListener('shown.bs.modal', (e) => {
  const modal = e.target;
  if (modal && modal.matches && modal.matches('[data-conn-movie]')) attach(modal);
});
